import { apiFetch } from './http';

// ─────────────────────────────────────────────────────────────────────
// UPLOADING A CLUB'S MEMBER LIST — the browser half of `POST /members/import`.
//
// The club pastes names and addresses, or picks a CSV; this turns the text into
// rows, asks the server what each row WOULD do (`dryRun`), and — once the club
// has seen that — asks it to do it. The same planner answers both calls, so the
// preview is the contract.
//
// NO CLUB TO NAME, as with the roster beside it: `apiFetch` carries the
// organization header and the server acts in the scope it resolves.
// ─────────────────────────────────────────────────────────────────────

/** As the server's `MAX_IMPORT_ROWS` — the browser says so before sending. */
export const MAX_IMPORT_ROWS = 500;

export interface MemberImportRow {
  name: string;
  email: string;
}

/** What one row will do, or did. The server's vocabulary, never translated here. */
export type MemberImportOutcome =
  | 'new'
  | 'member_invited'
  | 'invited'
  | 'already_member'
  | 'pending'
  | 'ambiguous'
  | 'invalid'
  | 'failed';

export interface MemberImportResultRow {
  index: number;
  name: string;
  email: string;
  outcome: MemberImportOutcome;
  /** For `invalid`: why. */
  reason?: string;
  /** For a matched row: how the club's own records found the player. */
  matchedBy?: 'email' | 'name';
  /** The player this row is, when the club already knew them. */
  player?: { id: string; name: string };
  /** Whether the mail went. Absent for a row that sent nothing. */
  sent?: boolean;
  /** The player this row CREATED, in the list's shape, so the list can show them. */
  created?: Record<string, unknown>;
}

export interface MemberImportResult {
  dryRun: boolean;
  rows: MemberImportResultRow[];
  summary: Record<string, number>;
}

export type MemberImportOutcomeEnvelope =
  | { status: 'ok'; result: MemberImportResult }
  /** Signed out, or no `member.manage` here. */
  | { status: 'refused'; reason: string }
  /** The server answered and a row was not the contract. The whole answer fails. */
  | { status: 'protocol' }
  | { status: 'unreachable' };

const OUTCOMES: readonly string[] = [
  'new',
  'member_invited',
  'invited',
  'already_member',
  'pending',
  'ambiguous',
  'invalid',
  'failed',
];

function readRow(raw: unknown): MemberImportResultRow | null {
  const r = (raw ?? {}) as Record<string, unknown>;
  if (typeof r.index !== 'number') return null;
  if (typeof r.name !== 'string' || typeof r.email !== 'string') return null;
  if (typeof r.outcome !== 'string' || !OUTCOMES.includes(r.outcome)) return null;
  const player = (r.player ?? null) as Record<string, unknown> | null;
  return {
    index: r.index,
    name: r.name,
    email: r.email,
    outcome: r.outcome as MemberImportOutcome,
    ...(typeof r.reason === 'string' ? { reason: r.reason } : {}),
    ...(r.matchedBy === 'email' || r.matchedBy === 'name' ? { matchedBy: r.matchedBy } : {}),
    ...(player && typeof player.id === 'string' && typeof player.name === 'string'
      ? { player: { id: player.id, name: player.name } }
      : {}),
    ...(typeof r.sent === 'boolean' ? { sent: r.sent } : {}),
    ...(r.created && typeof r.created === 'object'
      ? { created: r.created as Record<string, unknown> }
      : {}),
  };
}

async function post(
  rows: MemberImportRow[],
  dryRun: boolean,
): Promise<MemberImportOutcomeEnvelope> {
  let res: Response;
  try {
    res = await apiFetch('/members/import', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rows, dryRun }),
    });
  } catch {
    return { status: 'unreachable' };
  }
  if (res.status === 401 || res.status === 403) return { status: 'refused', reason: 'forbidden' };
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  if (!res.ok) {
    // The server's CODE, for `errorMessage` to say in the club's language (#519).
    return typeof body?.error === 'string'
      ? { status: 'refused', reason: body.error }
      : { status: 'unreachable' };
  }
  if (!Array.isArray(body?.rows) || typeof body?.summary !== 'object')
    return { status: 'protocol' };
  const parsed = body.rows.map(readRow);
  if (parsed.some((r) => r === null)) return { status: 'protocol' };
  return {
    status: 'ok',
    result: {
      dryRun: body.dryRun === true,
      rows: parsed as MemberImportResultRow[],
      summary: (body.summary ?? {}) as Record<string, number>,
    },
  };
}

/** What every row WOULD do. Writes nothing, sends nothing. */
export const previewMemberImport = (rows: MemberImportRow[]) => post(rows, true);
/** Do it: memberships, invitations and mail, row by row. */
export const importMembers = (rows: MemberImportRow[]) => post(rows, false);

// ─────────────────────────────────────────────────────────────────────
// CARRYING THE LIST OUT IN CHUNKS (#1139).
//
// The server carries out at most `MAX_IMPORT_APPLY_ROWS` rows per request:
// every row sends its invitation mail inside the request, and App Service ends
// a request at about 230 s. A 500-row confirm used to run past that — the
// browser saw a network error while the server went on working.
//
// SO THE PREVIEW DECIDES WHAT IS SENT. Only the rows it planned as actionable
// (`new`, `member_invited`, `invited`) go out, ten at a time; every other row
// keeps its preview answer. That keeps what only the whole list can tell —
// `duplicate_in_upload` above all — instead of re-planning each chunk on its own.
// Each chunk is re-planned by the server against the club as it is NOW, so a
// row that changed since the preview is reported as what actually happened.
//
// A CHUNK THAT FAILS STOPS THE RUN and says how far it got. Nothing is undone and
// nothing needs to be: confirming again is safe, because a row already carried
// out comes back `pending` (an invitation is live) or `already_member`, and is
// never sent twice.
// ─────────────────────────────────────────────────────────────────────

/** As the server's `MAX_IMPORT_APPLY_ROWS`. */
export const MAX_IMPORT_APPLY_ROWS = 10;

const ACTIONABLE_OUTCOMES = new Set<MemberImportOutcome>(['new', 'member_invited', 'invited']);

export type ChunkedImportOutcome =
  | { status: 'ok'; result: MemberImportResult }
  /** A chunk failed after `done` of `total` actionable rows were carried out. */
  | {
      status: 'stopped';
      done: number;
      total: number;
      cause: Exclude<MemberImportOutcomeEnvelope, { status: 'ok' }>;
    };

/**
 * Carry out the rows the preview found actionable, in server-sized chunks.
 *
 * `rows` is the list as previewed and `preview` the server's plan for it, whose
 * `index` is each row's position in `rows`. The result has one row per input
 * row, in order, with that same index.
 */
export async function importMembersChunked(
  rows: MemberImportRow[],
  preview: MemberImportResult,
  onProgress?: (done: number, total: number) => void,
): Promise<ChunkedImportOutcome> {
  const byIndex = new Map(preview.rows.map((r) => [r.index, r]));
  const todo = preview.rows.filter((r) => ACTIONABLE_OUTCOMES.has(r.outcome)).map((r) => r.index);
  let done = 0;
  for (let at = 0; at < todo.length; at += MAX_IMPORT_APPLY_ROWS) {
    const chunk = todo.slice(at, at + MAX_IMPORT_APPLY_ROWS);
    const answer = await post(
      chunk.map((i) => rows[i]!),
      false,
    );
    if (answer.status !== 'ok')
      return { status: 'stopped', done, total: todo.length, cause: answer };
    // The server numbers the chunk's rows from 0; put each back where it came from.
    for (const r of answer.result.rows) {
      const original = chunk[r.index];
      if (original === undefined) {
        return { status: 'stopped', done, total: todo.length, cause: { status: 'protocol' } };
      }
      byIndex.set(original, { ...r, index: original });
    }
    done += chunk.length;
    onProgress?.(done, todo.length);
  }
  const merged = [...byIndex.values()].sort((a, b) => a.index - b.index);
  const summary: Record<string, number> = {};
  for (const r of merged) summary[r.outcome] = (summary[r.outcome] ?? 0) + 1;
  return { status: 'ok', result: { dryRun: false, rows: merged, summary } };
}

// ─────────────────────────────────────────────────────────────────────
// PARSING WHAT A CLUB PASTES.
//
// A member list arrives as whatever the club's system exported: a CSV with a
// header, a sheet pasted as tab-separated text, or «Navn, e-post» typed by
// hand. The rule is deliberately simple and shown back as a preview rather than
// clever and silent: one person per line; the field with an `@` is the
// address; everything else on the line, joined, is the name.
// ─────────────────────────────────────────────────────────────────────

/** Words a header row is made of. A line that is only these is not a person. */
const HEADER_WORDS = new Set([
  'navn',
  'name',
  'fornavn',
  'etternavn',
  'firstname',
  'lastname',
  'first',
  'last',
  'e-post',
  'epost',
  'email',
  'e-mail',
  'mail',
  'medlem',
  'member',
]);

/** Split one line on the separator it uses: tab, semicolon or comma — in that order. */
function splitLine(line: string): string[] {
  const sep = line.includes('\t') ? '\t' : line.includes(';') ? ';' : ',';
  return line
    .split(sep)
    .map((f) =>
      f
        .trim()
        .replace(/^"(.*)"$/, '$1')
        .trim(),
    )
    .filter((f) => f !== '');
}

/**
 * Text → rows. Lines with nothing on them and a header line are dropped; a
 * line with no `@` field becomes a row with an empty address, so the server
 * reports it as invalid rather than this silently losing a person.
 */
export function parseMemberList(text: string): MemberImportRow[] {
  const rows: MemberImportRow[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const fields = splitLine(raw);
    if (fields.length === 0) continue;
    const isHeader =
      rows.length === 0 &&
      fields.every((f) => HEADER_WORDS.has(f.toLowerCase().replace(/[^a-zæøå-]/g, '')));
    if (isHeader) continue;
    const emailIndex = fields.findIndex((f) => f.includes('@'));
    const email = emailIndex >= 0 ? fields[emailIndex]! : '';
    const name = fields
      .filter((_, i) => i !== emailIndex)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
    rows.push({ name, email });
  }
  return rows;
}
